import React, { useRef } from "react";
import { InlineMath } from "react-katex";

/**
 * Oś liczbowa z wyróżnionym przedziałem:
 *  - pin + kółko w punkcie t,
 *  - wypełnienie NAD osią w stronę rozwiązania,
 *  - etykiety (0 i t) zawsze w pełni widoczne — dynamiczne guttery i dolny margines,
 *  - strzałka ZAWSZE po prawej i w prawo,
 *  - dla otwartego kółka: „dziura” w wypełnieniu pod kółkiem,
 *  - dynamiczna skala: dobieramy margines tak, by odległość w px między tickiem 0 a tickiem t była ≥ minTickGapPx.
 *
 * iOS fix:
 *  - ułamki \\dfrac/\\frac renderujemy jako czyste SVG (licznik/kreska/mianownik) zamiast foreignObject.
 */
export default function NumberLineInterval({
  t = 0,
  dir = "right", // "right" => [t, +∞) / (t, +∞), "left" => (-∞, t] / (-∞, t)
  closed = true,
  width = 520,
  height = 120,
  color = "#f59e0b",
  margin = 3.2,
  marginMin = 1.0,
  marginMax = 4.5,
  minTickGapPx = 92,
  showZero = true,
  tLabelLatex,

  // typografia jak w pozostałych komponentach
  fontSize = 16,
  fontFamily = "KaTeX_Main, STIXGeneral, 'Times New Roman', serif",
  useMathMinus = true,
}) {
  /* ── stałe rysunkowe ─────────────────────────────────────────────── */
  const AR = 10;
  const pinR = 7;
  const strokeW = 3;
  const halfW = strokeW / 2;

  /* ── helpers: minus + fraction SVG label ─────────────────────────── */
  const normalizeMinus = (txt) =>
    useMathMinus ? String(txt ?? "").replace(/-/g, "−").trim() : String(txt ?? "").trim();

  const isSimpleNumericLatex = (latex) => {
    if (!latex) return false;
    const s = normalizeMinus(
      String(latex ?? "")
        .replace(/\\,/g, " ")
        .replace(/[{}]/g, "")
        .replace(/\s+/g, " ")
        .trim()
    );
    return /^[0-9−+\-., ]+$/.test(s);
  };

  //  - "\\dfrac{2}{5}" / "\\frac{2}{5}"
  //  - "1\\dfrac{1}{4}" / "-3\\frac{1}{2}"
  //  - "-\\dfrac{1}{2}" / "−\\dfrac{1}{2}"
  const parseFractionLatex = (latexRaw) => {
    if (!latexRaw) return null;

    let s = String(latexRaw)
      .replace(/\\,/g, " ")
      .replace(/\\;/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    // unicode minus -> ASCII na czas parsowania
    const sParse = s.replace(/−/g, "-");

    const mixed = sParse.match(/^([+\-]?\d+)\s*(\\(?:d)?frac\{[^}]+\}\{[^}]+\})$/);

    let whole = null;
    let fracPart = sParse;

    if (mixed) {
      whole = mixed[1];
      fracPart = mixed[2];
    } else {
      const signOnly = sParse.match(/^([+\-])\s*(\\(?:d)?frac\{[^}]+\}\{[^}]+\})$/);
      if (signOnly) {
        whole = signOnly[1]; // sam znak jako "whole"
        fracPart = signOnly[2];
      }
    }

    const frac = fracPart.match(/\\(?:d)?frac\{([^}]+)\}\{([^}]+)\}/);
    if (!frac) return null;

    const num = String(frac[1]).replace(/[{}]/g, "").trim();
    const den = String(frac[2]).replace(/[{}]/g, "").trim();

    return {
      whole: whole != null ? normalizeMinus(String(whole).trim()) : null,
      num: normalizeMinus(num),
      den: normalizeMinus(den),
    };
  };

  const approxTextW = (txt, fs) => {
    const t = String(txt ?? "");
    return Math.max(0, t.length * fs * 0.58);
  };

  const FractionSvgLabel = ({ x, y, frac, fs = fontSize }) => {
    if (!frac) return null;

    const whole = frac.whole;
    const num = frac.num;
    const den = frac.den;

    const fsWhole = fs;
    const fsFrac = Math.max(11, Math.round(fs * 0.78));

    const gap = Math.round(fs * 0.34);
    const fracPad = Math.max(6, Math.round(fs * 0.22));

    const wholeW = whole ? approxTextW(whole, fsWhole) : 0;
    const numW = approxTextW(num, fsFrac);
    const denW = approxTextW(den, fsFrac);

    const fracW = Math.max(numW, denW) + fracPad * 2;
    const totalW = whole ? wholeW + gap + fracW : fracW;

    const startX = -totalW / 2;
    const fracXCenter = startX + (whole ? wholeW + gap + fracW / 2 : fracW / 2);

    const numY = 0;
    const lineY = fsFrac * 1.05;
    const denY = fsFrac * 1.35;

    return (
      <g transform={`translate(${x} ${y})`} style={{ pointerEvents: "none" }}>
        {whole && (
          <text
            x={startX}
            y={Math.round((denY + fsFrac) / 2)}
            textAnchor="start"
            dominantBaseline="middle"
            fontSize={fsWhole}
            fontFamily={fontFamily}
            fill="currentColor"
          >
            {whole}
          </text>
        )}

        <text
          x={fracXCenter}
          y={numY}
          textAnchor="middle"
          dominantBaseline="hanging"
          fontSize={fsFrac}
          fontFamily={fontFamily}
          fill="currentColor"
        >
          {num}
        </text>

        <line
          x1={fracXCenter - fracW / 2}
          y1={lineY}
          x2={fracXCenter + fracW / 2}
          y2={lineY}
          stroke="currentColor"
          strokeWidth={Math.max(1.6, Math.round(fs * 0.08))}
          strokeLinecap="round"
        />

        <text
          x={fracXCenter}
          y={denY}
          textAnchor="middle"
          dominantBaseline="hanging"
          fontSize={fsFrac}
          fontFamily={fontFamily}
          fill="currentColor"
        >
          {den}
        </text>
      </g>
    );
  };

  /* ── estymacja etykiet → guttery ─────────────────────────────────── */
  const approxLabelWidth = (latex) => {
    const s = String(latex || "");
    const extra = s.includes("\\dfrac") || s.includes("\\frac") ? 48 : 28;
    return Math.max(90, Math.min(240, 8 * s.length + extra));
  };

  const labelW0 = showZero ? 72 : 0;
  const labelWT = approxLabelWidth(tLabelLatex ?? String(t));
  const labelH = 48;
  const labelYOffset = 12;

  const baseGutter = 24;
  const halfMaxLbl = Math.max(labelW0, labelWT) / 2;
  const padLeft = baseGutter + halfMaxLbl + 8;
  const padRight = baseGutter + halfMaxLbl + 8;

  /* ── geometria osi ──────────────────────────────────────────────── */
  const baseAxisFrac = 0.65;
  let axisY = Math.round(height * baseAxisFrac);
  const requiredBottom = labelYOffset + labelH + 4;
  if (height - axisY < requiredBottom) axisY = height - requiredBottom;

  const leftX = padLeft;
  const rightX = width - padRight;
  const axisPxW = Math.max(10, rightX - leftX);

  /* ── dynamiczny margines ────────────────────────────────────────── */
  const absT = Math.abs(t);
  let mEff = Math.max(marginMin, Math.min(marginMax, margin));
  if (absT > 0 && axisPxW > 0) {
    const maxByGap = (axisPxW * absT / minTickGapPx - absT) / 2;
    const capped = isFinite(maxByGap) ? Math.max(marginMin, Math.min(marginMax, maxByGap)) : marginMin;
    mEff = Math.min(mEff, capped);
  } else {
    mEff = marginMin;
  }

  const xMin = Math.min(0, t) - mEff;
  const xMax = Math.max(0, t) + mEff;

  const xToPx = (x) => leftX + ((x - xMin) / (xMax - xMin)) * axisPxW;

  const x0 = xToPx(0);
  const xT = xToPx(t);

  /* ── wypełnienie rozwiązania (nad osią) ──────────────────────────── */
  // Wypelnienie ma pokrywac caly "przedzial roboczy" od osi do gornej kreski.
  // Dzieki temu nie ma efektu dwoch oddzielnych paskow.
  const pinTopY = axisY - 26;
  const fillY = pinTopY;
  const fillH = Math.max(0, axisY - pinTopY);
  const fillX = dir === "right" ? xT : leftX;
  const fillW = dir === "right" ? rightX - xT : xT - leftX;
  const fillRadius = 6;

  /* ── pin w punkcie t ─────────────────────────────────────────────── */

  /* ── maska „dziury” pod otwartym kółkiem ────────────────────────── */
  const maskIdRef = useRef(`nl-mask-${Math.random().toString(36).slice(2)}`);

  // Linia pozioma: nie przechodzi przez środek otwartego kółka
  const capStartX = closed ? xT : dir === "right" ? xT + pinR + halfW : xT - pinR - halfW;
  const pinTopForLine = closed ? pinTopY : pinTopY + pinR - halfW;

  /* ── etykieta t: wybór renderu ──────────────────────────────────── */
  const tLabelRaw = tLabelLatex ?? String(t);
  const tFrac = parseFractionLatex(tLabelRaw);

  const tPlain = !tFrac && isSimpleNumericLatex(tLabelRaw)
    ? normalizeMinus(
        String(tLabelRaw ?? "")
          .replace(/\\,/g, " ")
          .replace(/[{}]/g, "")
          .replace(/\s+/g, " ")
          .trim()
      )
    : null;

  // foreignObject clamp
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const labelTX = clamp(xT - labelWT / 2, 0, width - labelWT);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label={`oś liczbowa: t = ${t}`}>
        {/* defs: maska z „dziurą” pod otwarte kółko */}
        {!closed && (
          <defs>
            <mask id={maskIdRef.current}>
              <rect x="0" y="0" width={width} height={height} fill="white" />
              <circle cx={xT} cy={pinTopY} r={pinR + 1} fill="black" />
            </mask>
          </defs>
        )}

        {/* wypełnienie rozwiązania */}
        {fillW > 0 && (
          <rect
            x={fillX}
            y={fillY}
            width={Math.max(0, fillW)}
            height={Math.max(0, fillH)}
            fill={color}
            fillOpacity="0.25"
            stroke={color}
            strokeOpacity="0.65"
            strokeWidth="2"
            rx={fillRadius}
            {...(!closed ? { mask: `url(#${maskIdRef.current})` } : {})}
          />
        )}

        {/* oś */}
        <line x1={leftX} y1={axisY} x2={rightX} y2={axisY} stroke="currentColor" strokeWidth="2" />

        {/* strzałka zawsze po prawej */}
        <path
          d={`M ${rightX - AR} ${axisY - AR / 2} L ${rightX} ${axisY} L ${rightX - AR} ${axisY + AR / 2} Z`}
          fill="currentColor"
        />

        {/* ticki */}
        {showZero && <line x1={x0} y1={axisY - 6} x2={x0} y2={axisY + 6} stroke="currentColor" strokeWidth="2" />}
        <line x1={xT} y1={axisY - 6} x2={xT} y2={axisY + 6} stroke="currentColor" strokeWidth="2" />

        {/* pin pionowy */}
        <line x1={xT} y1={axisY} x2={xT} y2={pinTopForLine} stroke={color} strokeWidth={strokeW} />

        {/* pozioma kreska */}
        <line x1={capStartX} y1={pinTopY} x2={dir === "right" ? rightX : leftX} y2={pinTopY} stroke={color} strokeWidth={strokeW} strokeLinecap="round" />

        {/* kółko w punkcie t (OTWARTE: przezroczyste) */}
        {closed ? (
          <circle cx={xT} cy={pinTopY} r={pinR} fill={color} />
        ) : (
          <circle cx={xT} cy={pinTopY} r={pinR} fill="none" stroke={color} strokeWidth={strokeW} />
        )}

        {/* etykieta 0 */}
        {showZero && (
          <text
            x={x0}
            y={axisY + labelYOffset}
            textAnchor="middle"
            dominantBaseline="hanging"
            fontSize={fontSize}
            fontFamily={fontFamily}
            fill="currentColor"
            style={{ pointerEvents: "none" }}
          >
            0
          </text>
        )}

        {/* etykieta t */}
        {tFrac ? (
          <FractionSvgLabel x={xT} y={axisY + labelYOffset} frac={tFrac} fs={fontSize} />
        ) : tPlain ? (
          <text
            x={xT}
            y={axisY + labelYOffset}
            textAnchor="middle"
            dominantBaseline="hanging"
            fontSize={fontSize}
            fontFamily={fontFamily}
            fill="currentColor"
            style={{ pointerEvents: "none" }}
          >
            {tPlain}
          </text>
        ) : (
          <foreignObject x={labelTX} y={axisY + labelYOffset} width={labelWT} height={labelH} pointerEvents="none">
            <div style={{ textAlign: "center", pointerEvents: "none" }}>
              <InlineMath math={normalizeMinus(tLabelRaw)} />
            </div>
          </foreignObject>
        )}
      </svg>
    </div>
  );
}
